/**
 * Surfacing the ICD 9.3 finalisation refusals (HG-3B G3-09).
 *
 * Migration 251 makes finalize_session refuse a publication that would violate
 * either ICD 9.3 invariant, and it names every competency responsible in the
 * message. All four callers of that RPC, three web and one mobile, previously
 * replaced any error with the fixed sentence "Failed to finalize session",
 * which is correct for an outage and useless for a refusal: an instructor told
 * only that it failed has no way to learn that FPM has no dimensions recorded.
 *
 * A named error nobody can read is not a named error, so this module decides,
 * once for both platforms, which messages are safe to show verbatim.
 *
 * Deliberately a whitelist, not a passthrough. A database error message can
 * carry schema detail, constraint names or row values that do not belong in
 * front of an instructor, so only the two codes this migration defines are
 * forwarded; everything else keeps the generic sentence it has today.
 */

/** The two named refusals migration 251 raises, one per ICD 9.3 invariant. */
export const FINALISATION_REFUSAL_CODES = [
  'ERR_DIMENSIONS_REQUIRED_ON_FINALISE',
  'ERR_OVERRIDE_FLAG_REQUIRED_ON_FINALISE',
] as const;

export type FinalisationRefusalCode = (typeof FINALISATION_REFUSAL_CODES)[number];

/** What the caller should tell the user, and whether it is the user's to fix. */
export interface FinalisationErrorMessage {
  /** The sentence to show. */
  message: string;
  /**
   * True where the message came from a named ICD 9.3 refusal, so the caller can
   * present it as something to correct rather than as a failure to retry.
   */
  refusal: boolean;
  code: FinalisationRefusalCode | null;
}

const GENERIC = 'Failed to finalize session. Please try again.';

function codeIn(text: string): FinalisationRefusalCode | null {
  return FINALISATION_REFUSAL_CODES.find((c) => text.includes(c)) ?? null;
}

/**
 * Turn whatever the RPC returned into something to put in front of an
 * instructor.
 *
 * Accepts an unknown because the three web callers get a PostgrestError, the
 * mobile caller gets one from its own client, and a thrown exception can arrive
 * here too. All that matters is whether a message string carries one of the two
 * codes.
 */
export function describeFinalisationError(error: unknown): FinalisationErrorMessage {
  const raw =
    typeof error === 'string'
      ? error
      : typeof (error as { message?: unknown })?.message === 'string'
        ? ((error as { message: string }).message)
        : '';

  const code = raw ? codeIn(raw) : null;
  if (!code) return { message: GENERIC, refusal: false, code: null };

  // The migration writes the sentence an instructor needs, competency names
  // included, after the code and its colon. Strip the code: it is for logs and
  // for this function, not for the person reading the alert.
  const afterCode = raw.slice(raw.indexOf(code) + code.length).replace(/^:\s*/, '').trim();

  return {
    message: afterCode.length > 0 ? afterCode : GENERIC,
    refusal: true,
    code,
  };
}
